// Held-map HUD (UI-8 / D15, M18). When the player holds a `filled_map` (main hand
// or offhand), this renders that map's world-locked 128×128 snapshot bottom-right,
// with a player pointer that rotates with facing and clamps to the edge when the
// player walks off the mapped area. The snapshot fills in as the player explores.
//
// Presentation only (ENG-1): all sampling/relief-shading lives in the pure
// cartography engine; this just blits `MapSnapshot.rgba` and draws the pointer.
import { CARTO } from '../../../sim/config';
import { INV, isEmpty } from '../../../sim/game/inventory';
import type { MapPalette, MapSampler, MapStore } from '../../../sim/game/cartography';
import type { Game } from '../../boot';

const SIZE = 160; // on-screen px (128 map pixels upscaled)
const PARCHMENT = '#c9b98a';

export class MapView {
  private readonly root: HTMLDivElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly img: HTMLCanvasElement;
  private readonly imgCtx: CanvasRenderingContext2D;
  private visible = false;

  constructor(
    private readonly game: Game,
    mount: HTMLElement,
    private readonly store: MapStore,
    private readonly palette: MapPalette,
    private readonly sampler: MapSampler,
  ) {
    this.root = document.createElement('div');
    this.root.id = 'bb-mapview';
    this.root.style.cssText =
      `position:fixed;bottom:64px;right:12px;width:${SIZE}px;height:${SIZE}px;pointer-events:none;` +
      'display:none;transform:scale(var(--bb-scale,1));transform-origin:bottom right;';

    this.canvas = document.createElement('canvas');
    this.canvas.width = SIZE;
    this.canvas.height = SIZE;
    this.canvas.style.cssText = 'width:100%;height:100%;image-rendering:pixelated;border:2px solid #6b4b2b;box-shadow:0 2px 8px rgba(0,0,0,.5);';
    this.ctx = this.canvas.getContext('2d')!;
    this.ctx.imageSmoothingEnabled = false;

    this.img = document.createElement('canvas');
    this.img.width = CARTO.MAP_PX;
    this.img.height = CARTO.MAP_PX;
    this.imgCtx = this.img.getContext('2d')!;

    this.root.appendChild(this.canvas);
    mount.appendChild(this.root);
  }

  /** mapId of a `filled_map` in hand or offhand, or null. */
  private heldMapId(): number | null {
    const inv = this.game.playerState.inventory;
    for (const stack of [inv.held(), inv.get(INV.OFFHAND)]) {
      if (isEmpty(stack)) continue;
      if (this.game.registries.items.nameOf[stack.item] !== 'filled_map') continue;
      const id = stack.components?.mapId;
      if (id !== undefined) return id;
    }
    return null;
  }

  update(): void {
    const id = this.heldMapId();
    const snap = id !== null ? this.store.get(id) : undefined;
    if (!snap) {
      if (this.visible) { this.visible = false; this.root.style.display = 'none'; }
      return;
    }
    if (!this.visible) { this.visible = true; this.root.style.display = 'block'; }

    const { body, yaw } = this.game.player;
    snap.refresh(this.sampler, this.palette, Math.floor(body.x), Math.floor(body.z), CARTO.UPDATE_RADIUS);

    // Parchment backdrop, then the mapped pixels over it (blank pixels stay blank).
    const ctx = this.ctx;
    ctx.fillStyle = PARCHMENT;
    ctx.fillRect(0, 0, SIZE, SIZE);
    this.imgCtx.putImageData(new ImageData(new Uint8ClampedArray(snap.rgba), CARTO.MAP_PX, CARTO.MAP_PX), 0, 0);
    ctx.drawImage(this.img, 0, 0, CARTO.MAP_PX, CARTO.MAP_PX, 0, 0, SIZE, SIZE);

    // Player pointer: on-map it rotates with facing; off-map it clamps to the edge.
    const scale = SIZE / CARTO.MAP_PX;
    // Treasure/structure target (M18): a red "X" pinned at the map's target, clamped
    // to the edge when it lies beyond the mapped area.
    if (snap.target) {
      const t = snap.targetMarker(snap.target.x, snap.target.z);
      const tx = Math.max(5, Math.min(CARTO.MAP_PX - 5, t.u)) * scale;
      const ty = Math.max(5, Math.min(CARTO.MAP_PX - 5, t.v)) * scale;
      ctx.strokeStyle = '#7a1f1f'; ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(tx - 5, ty - 5); ctx.lineTo(tx + 5, ty + 5);
      ctx.moveTo(tx + 5, ty - 5); ctx.lineTo(tx - 5, ty + 5);
      ctx.stroke();
    }
    const m = snap.marker(body.x, body.z, yaw);
    const u = Math.max(6, Math.min(CARTO.MAP_PX - 6, m.u));
    const v = Math.max(6, Math.min(CARTO.MAP_PX - 6, m.v));
    const cx = u * scale, cy = v * scale;
    const fx = -Math.sin(yaw), fy = -Math.cos(yaw);
    const rx = -fy, ry = fx;
    ctx.fillStyle = m.inBounds ? '#ff5252' : '#ffd24a';
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(cx + fx * 8, cy + fy * 8);
    ctx.lineTo(cx - fx * 6 + rx * 5, cy - fy * 6 + ry * 5);
    ctx.lineTo(cx - fx * 6 - rx * 5, cy - fy * 6 - ry * 5);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
}
